/**
 * Live "free shipping remaining" countdown.
 *
 * The server renders the correct text for the current cart (see
 * `snippets/free-shipping-message.liquid`), so the first paint is always right.
 * This module only keeps it in sync after AJAX cart changes (add to cart,
 * quantity update) — the announcement bar lives in the header and is not
 * re-rendered on `cart:update`, so it needs to be patched client-side.
 *
 * Money is formatted by mirroring two server-side `money` samples on each element
 * (`data-money-grp` / `data-money-dec`) — separators, decimals AND the currency
 * symbol. `| money` is presentment-aware, so the live amount is
 * character-for-character consistent with the rest of the store on every market.
 * Stejný vzor jako `assets/gift-progress-bar.js`.
 */
(function () {
  const SELECTOR = '[data-free-shipping]';

  /**
   * Derive separators, decimal count AND the currency symbol from the
   * server-rendered money samples.
   * `\s` in JS already covers regular space, nbsp (U+00A0) and narrow nbsp (U+202F).
   * @param {HTMLElement} el
   */
  function detectFormat(el) {
    const keep = /[^\d.,\s']/g;
    const grpRaw = el.dataset.moneyGrp || '';
    const decRaw = el.dataset.moneyDec || '';
    const grpNum = grpRaw.replace(keep, '').trim(); // e.g. "10 000" or "10 000,00"
    const decNum = decRaw.replace(keep, '').trim(); // e.g. "1" or "1,00"

    let decimals = 0;
    let decSep = '';
    const dec = decNum.match(/^\d+(\D)(\d+)$/);
    if (dec) {
      decSep = dec[1];
      decimals = dec[2].length;
    }

    let grpInt = grpNum;
    if (decimals > 0 && grpNum.length > decimals + 1) {
      grpInt = grpNum.slice(0, grpNum.length - decimals - 1);
    }
    const grp = grpInt.match(/\d+(\D)\d{3}/);
    const groupSep = grp ? grp[1] : '';

    // Symbol + jeho umístění (prefix/suffix) se odvozuje z `data-money-dec`. Ten jde přes
    // `| money`, a je tedy presentment-aware: cokoli kolem číselné části toho vzorku patří
    // AKTUÁLNÍ měně košíku, ať je to CZK, EUR nebo cokoli dalšího. Díky tomu nemusí do DOM
    // zvlášť formátovací řetězec. Stejné řešení jako `assets/gift-progress-bar.js`.
    //
    // POZOR na historii (2026-09-14): tenhle modul dřív bral formát z
    // `data-money-format="{{ shop.money_format }}"` a to bylo označeno za chybu „na SK
    // napíše Kč". Byla to CHYBNÁ DIAGNÓZA — `{{ shop.money_format }}` se na storefrontu řídí
    // trhem požadavku (změřeno na royalbay.sk / `royalbay.cz/?country=SK` → EUR formát);
    // formát obchodu vrací až Admin API. Obě varianty jsou správné, tahle je jen kratší.
    // Viz `docs/audit-doprava-zdarma-sk-2026-09-14.md` §7.
    let prefix = '';
    let suffix = '';
    const digits = decRaw.match(/\d(?:[\d.,\s']*\d)?/);
    if (digits) {
      prefix = decRaw.slice(0, digits.index);
      suffix = decRaw.slice(digits.index + digits[0].length);
    }

    return { decimals, decSep, groupSep, prefix, suffix };
  }

  /**
   * Format a minor-unit amount the way the server would for the CURRENT cart currency.
   * @param {number} cents
   * @param {HTMLElement} el
   */
  function formatMoney(cents, el) {
    const { decimals, decSep, groupSep, prefix, suffix } = detectFormat(el);
    const value = (cents / 100).toFixed(decimals);
    let [intPart, decPart] = value.split('.');
    if (groupSep) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    const number = decimals > 0 ? intPart + decSep + decPart : intPart;
    return prefix + number + suffix;
  }

  /**
   * Rewrite every free-shipping countdown element for the given cart total.
   * @param {number} totalCents
   */
  function updateMessages(totalCents) {
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      const threshold = parseInt(el.dataset.threshold || '0', 10);
      if (!threshold) return;

      const remaining = threshold - totalCents;
      if (remaining > 0) {
        el.textContent = (el.dataset.remainingText || '').replace('[castka]', formatMoney(remaining, el));
      } else {
        el.textContent = el.dataset.successText || '';
      }
    });
  }

  /** Fetch the current cart total and refresh the messages. */
  function refreshFromCart() {
    if (document.querySelector(SELECTOR) === null) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (cart) updateMessages(cart.total_price || 0);
      })
      .catch(function () {
        // Network hiccup — leave the server-rendered text in place.
      });
  }

  document.addEventListener('cart:update', refreshFromCart);
})();
